import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
} from 'reactstrap';
import { useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import AvatarGroup from 'views/Widgets/People/AvatarGroup';
import { AGENT_TEMPLATES, TONE_PRESETS } from './consts';

interface Props {
  value?: any;
  object?: any;
  name?: string;
  onActionComplete?: () => void;
}

const ConfigurationRecap: React.FC<Props> = ({
  object = {},
  onActionComplete,
}) => {
  const {
    id,
    initial_template,
    name,
    description,
    config_purpose,
    config_responsibilities,
    config_guidelines,
    config_custom_instructions,
    owners,
    visibility,
    viewers,
    associated_documents,
    associated_skills,
    custom_skills,
    tone_preset,
    challenge_level,
    empathy_balance,
    directness,
    formality,
    proactivity,
    status,
  } = object;

  const getVisibilityLabel = (vis: string) => {
    switch (vis) {
      case 'I':
        return (
          <FormattedMessage
            id="app.views.ai.agents.agents_admin.visibility.private"
            defaultMessage="Private"
          />
        );
      case 'P':
        return (
          <FormattedMessage
            id="app.views.ai.agents.agents_admin.visibility.public"
            defaultMessage="Public"
          />
        );
      case 'C':
        return (
          <FormattedMessage
            id="app.views.ai.agents.agents_admin.visibility.custom"
            defaultMessage="Custom"
          />
        );
      default:
        return vis;
    }
  };

  const renderSliderValue = (value: number | undefined) => {
    if (typeof value === 'undefined') return '-';
    return `${Math.round(value * 10)}%`;
  };

  const RecapSection: React.FC<{
    title: string;
    children: React.ReactNode;
  }> = ({ title, children }) => (
    <Card className="mb-4">
      <CardHeader>{title}</CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );

  const RecapField: React.FC<{
    label: string;
    value: React.ReactNode;
    fullWidth?: boolean;
  }> = ({ label, value, fullWidth = false }) => (
    <Col md={fullWidth ? 12 : 6} className="mb-3">
      <div className="text-muted small mb-1">{label}</div>
      <div className="fw-medium">{value || '-'}</div>
    </Col>
  );

  const proxyParameters = useProxyParameters();

  const handleOnClickInitialize = useCallback(() => {
    if (!id) return;
    ConfirmAPI.sendRequestToConfirm(
      'POST',
      `/agents/${id}/initialize`,
      proxyParameters,
      (_response, error) => {
        if (error) {
          console.error('Error initializing agent:', error);
          return;
        }
        onActionComplete?.();
      }
    );
  }, [id, proxyParameters, onActionComplete]);

  const handleOnClickDisable = useCallback(() => {
    if (!id) return;
    ConfirmAPI.sendRequestToConfirm(
      'POST',
      `/agents/${id}/disable`,
      proxyParameters,
      (_response, error) => {
        if (error) {
          console.error('Error disabling agent:', error);
          return;
        }
        onActionComplete?.();
      }
    );
  }, [id, proxyParameters, onActionComplete]);

  const isPublished = status === 'P' || status === 'M';

  const { formatMessage } = useIntl();

  return (
    <div>
      <Row>
        <Col className="col-12">
          <RecapSection
            title={formatMessage({
              id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.title.basic_information',
              defaultMessage: 'Basic Information',
            })}
          >
            <Row>
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.template',
                  defaultMessage: 'Template',
                })}
                value={
                  initial_template ? (
                    <Badge color="info" className="px-3 py-2">
                      {
                        AGENT_TEMPLATES(formatMessage).find(
                          (template) => template.id === initial_template
                        )?.name
                      }
                    </Badge>
                  ) : (
                    '-'
                  )
                }
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.name',
                  defaultMessage: 'Name',
                })}
                value={name}
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.description',
                  defaultMessage: 'Description',
                })}
                value={description}
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.purpose',
                  defaultMessage: 'Agent role & purpose',
                })}
                value={config_purpose}
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.responsibilities',
                  defaultMessage: 'Core responsibilities',
                })}
                value={config_responsibilities}
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.guidelines',
                  defaultMessage: 'How the agent should use company data',
                })}
                value={config_guidelines}
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.custom_instructions',
                  defaultMessage: 'Custom instructions',
                })}
                value={config_custom_instructions}
                fullWidth
              />
            </Row>
          </RecapSection>
        </Col>
        <Col className="col-12">
          <RecapSection
            title={formatMessage({
              id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.title.audience_access',
              defaultMessage: 'Audience & Access',
            })}
          >
            <Row>
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.owners',
                  defaultMessage: 'Owners',
                })}
                value={
                  owners && owners.length > 0 ? (
                    <div className="d-flex align-items-center">
                      <AvatarGroup people={owners} />
                    </div>
                  ) : (
                    '-'
                  )
                }
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.visibility',
                  defaultMessage: 'Visibility',
                })}
                value={getVisibilityLabel(visibility)}
              />
              {visibility === 'C' && (
                <RecapField
                  label={formatMessage({
                    id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.custom_viewers',
                    defaultMessage: 'Custom Viewers',
                  })}
                  value={
                    viewers ? (
                      <span className="text-muted small">
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap._custom_filter_applied_"
                          defaultMessage="
                    Custom filter applied
                  "
                        />
                      </span>
                    ) : (
                      '-'
                    )
                  }
                />
              )}
            </Row>
          </RecapSection>
        </Col>
      </Row>
      <Row>
        <Col className="col-12">
          <RecapSection
            title={formatMessage({
              id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.title.content_skills',
              defaultMessage: 'Content & Skills',
            })}
          >
            <Row>
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.documents',
                  defaultMessage: 'Documents',
                })}
                value={
                  associated_documents && associated_documents.length > 0 ? (
                    <div>
                      {associated_documents.map((doc: any) => (
                        <Badge
                          key={doc.id}
                          color="light"
                          className="me-2 mb-2 px-3 py-2"
                        >
                          <i className="fe fe-file-text me-2" />
                          {doc.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">
                      <FormattedMessage
                        id="app.views.a_i.agents.agents_admin.agent_configuration_recap.no_documents_selected"
                        defaultMessage="No documents selected"
                      />
                    </span>
                  )
                }
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.skills',
                  defaultMessage: 'Skills',
                })}
                value={
                  associated_skills && associated_skills.length > 0 ? (
                    <div>
                      {associated_skills.map((skill: any) => (
                        <Badge
                          key={skill.id}
                          color="light"
                          className="me-2 mb-2 px-3 py-2"
                        >
                          <i className="fe fe-zap me-2" />
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">
                      <FormattedMessage
                        id="app.views.a_i.agents.agents_admin.agent_configuration_recap.no_skills_selected"
                        defaultMessage="No skills selected"
                      />
                    </span>
                  )
                }
                fullWidth
              />
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.custom_skills',
                  defaultMessage: 'Custom Skills',
                })}
                value={
                  custom_skills && custom_skills.length > 0 ? (
                    <div>
                      {custom_skills.map((skill: any) => (
                        <Badge
                          key={skill.id}
                          color="light"
                          className="me-2 mb-2 px-3 py-2"
                        >
                          <i className="fe fe-zap me-2" />
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">
                      <FormattedMessage
                        id="app.views.a_i.agents.agents_admin.agent_configuration_recap.no_custom_skills_selected"
                        defaultMessage="No custom skills selected"
                      />
                    </span>
                  )
                }
                fullWidth
              />
            </Row>
          </RecapSection>
        </Col>
        <Col className="col-12">
          <RecapSection
            title={formatMessage({
              id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.title.behavior_tone',
              defaultMessage: 'Behavior & Tone',
            })}
          >
            <Row>
              <RecapField
                label={formatMessage({
                  id: 'app.views.a_i.agents.agents_admin.agent_configuration_recap.label.tone_preset',
                  defaultMessage: 'Tone Preset',
                })}
                value={
                  tone_preset ? (
                    <Badge color="info" className="px-3 py-2">
                      {
                        TONE_PRESETS(formatMessage).find(
                          (preset) => preset.id === tone_preset
                        )?.name
                      }
                    </Badge>
                  ) : (
                    '-'
                  )
                }
                fullWidth
              />
            </Row>
            <Row className="mt-3">
              <Col md={6} className="mb-3">
                <Card className="bg-light border-0">
                  <CardBody className="py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.challenge_level"
                          defaultMessage="Challenge Level"
                        />
                      </span>
                      <span className="badge bg-primary">
                        {renderSliderValue(challenge_level)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.supportive"
                          defaultMessage="Supportive"
                        />
                      </small>
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.push_thinking"
                          defaultMessage="Push thinking"
                        />
                      </small>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card className="bg-light border-0">
                  <CardBody className="py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.empathy_balance"
                          defaultMessage="Empathy Balance"
                        />
                      </span>
                      <span className="badge bg-primary">
                        {renderSliderValue(empathy_balance)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.matteroffact"
                          defaultMessage="Matter-of-fact"
                        />
                      </small>
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.warm_empathetic"
                          defaultMessage="Warm & empathetic"
                        />
                      </small>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card className="bg-light border-0">
                  <CardBody className="py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.directness"
                          defaultMessage="Directness"
                        />
                      </span>
                      <span className="badge bg-primary">
                        {renderSliderValue(directness)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.exploratory"
                          defaultMessage="Exploratory"
                        />
                      </small>
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.direct_concise"
                          defaultMessage="Direct & concise"
                        />
                      </small>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card className="bg-light border-0">
                  <CardBody className="py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.formality"
                          defaultMessage="Formality"
                        />
                      </span>
                      <span className="badge bg-primary">
                        {renderSliderValue(formality)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.conversational"
                          defaultMessage="Conversational"
                        />
                      </small>
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.policyguarded"
                          defaultMessage="Policy-guarded"
                        />
                      </small>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card className="bg-light border-0">
                  <CardBody className="py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.proactivity"
                          defaultMessage="Proactivity"
                        />
                      </span>
                      <span className="badge bg-primary">
                        {renderSliderValue(proactivity)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.reactive"
                          defaultMessage="Reactive"
                        />
                      </small>
                      <small>
                        <FormattedMessage
                          id="app.views.a_i.agents.agents_admin.agent_configuration_recap.anticipatory"
                          defaultMessage="Anticipatory"
                        />
                      </small>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </RecapSection>
        </Col>
      </Row>
      <Row>
        <Col className="col-12">
          <Card>
            <CardBody>
              <div className="d-flex align-items-start">
                <i
                  className="fe fe-info text-info me-3 mt-1"
                  style={{ fontSize: '1.25rem' }}
                />
                <div>
                  <p className="mb-2 fw-medium">
                    <FormattedMessage
                      id="app.views.a_i.agents.agents_admin.configuration_recap.ready_to_publish_title"
                      defaultMessage="Are you happy with these settings?"
                    />
                  </p>
                  <p className="text-muted mb-0">
                    <FormattedMessage
                      id="app.views.a_i.agents.agents_admin.configuration_recap.ready_to_publish_description"
                      defaultMessage="Initializing and publishing the agent may take a few minutes while everything is set up. You can click Save & Finish now and initialize it later from the actions menu in the agents list, or come back to the recap page to publish it when you're ready."
                    />
                  </p>
                </div>
              </div>
              {onActionComplete && (
                <div className="mt-3 text-end">
                  {isPublished ? (
                    <Button color="warning" onClick={handleOnClickDisable}>
                      <i className="fe fe-pause-circle me-2" />
                      <FormattedMessage
                        id="app.views.a_i.agents.agents_admin.configuration_recap.unpublish"
                        defaultMessage="Unpublish"
                      />
                    </Button>
                  ) : (
                    <Button color="primary" onClick={handleOnClickInitialize}>
                      <i className="fe fe-play me-2" />
                      <FormattedMessage
                        id="app.views.a_i.agents.agents_admin.configuration_recap.initialize_and_publish"
                        defaultMessage="Initialize & Publish"
                      />
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ConfigurationRecap;
